import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { NotificationsService, NotificationDto } from '../../../services/api/notifications';
import { LanguageService } from '../../../services/i18n/language';
import { formatRequestNumber, formatDocNumber } from '../../../services/request-number';

const POLL_INTERVAL_MS = 30000;

export interface NotificationGroup {
  key: string;
  /** Самое свежее уведомление группы — его заголовок/текст/время и показываем. */
  latest: NotificationDto;
  items: NotificationDto[];
  unreadCount: number;
}

/**
 * Колокольчик уведомлений в шапке — список персональных уведомлений пользователя
 * (см. NotificationEntity/PushNotificationService.sendToUser на бэкенде). Клик по
 * уведомлению переходит по его url (бэкенд уже формирует url с учётом роли
 * получателя) и помечает уведомление прочитанным — оно тут же пропадает из списка.
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification-bell.html',
  styleUrl: './notification-bell.scss'
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  open = false;
  loading = false;
  loadError: string | null = null;
  notifications: NotificationDto[] = [];
  groups: NotificationGroup[] = [];
  unreadCount = 0;

  emptyText = 'Уведомлений пока нет';
  loadErrorText = 'Не удалось загрузить уведомления';
  markAllReadText = 'Отметить все прочитанными';

  private subscriptions = new Subscription();

  constructor(
    private notificationsService: NotificationsService,
    private router: Router,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    this.refreshUnreadCount();
    this.subscriptions.add(
      interval(POLL_INTERVAL_MS).subscribe(() => this.refreshUnreadCount())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.elementRef.nativeElement.contains(event.target)) {
      this.open = false;
    }
  }

  private updateTranslations(): void {
    this.emptyText = this.languageService.translate('notifications.empty') || this.emptyText;
    this.loadErrorText = this.languageService.translate('notifications.loadError') || this.loadErrorText;
    this.markAllReadText = this.languageService.translate('notifications.markAllRead') || this.markAllReadText;
  }

  private refreshUnreadCount(): void {
    this.notificationsService.getUnreadCount().subscribe({
      next: (res) => {
        this.unreadCount = res.count;
        this.cdr.markForCheck();
      },
      error: () => {}
    });
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) {
      this.load();
    }
  }

  private load(): void {
    this.loading = true;
    this.loadError = null;
    this.notificationsService.getMyNotifications(0, 20).subscribe({
      next: (page) => {
        // Открытые (прочитанные) уведомления не должны снова появляться в колокольчике
        // при следующем открытии — список это то, что ещё не разобрано, а не история.
        const unread = page.content.filter(n => !n.read);
        this.notifications = unread;
        this.groups = this.groupNotifications(unread);
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadError = this.loadErrorText;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Несколько сообщений по одной и той же заявке/сделке — частый случай (переписка),
   * группируем в одну строку со счётчиком, не глядя на то, кто именно из экспедиторов
   * писал. Ключ группировки — (title, url): для одного типа уведомления по одной и
   * той же записи они всегда совпадают (кто отправитель — не влияет, это только в
   * тексте сообщения, который мы теперь не показываем). Так группируются и новые
   * уведомления с type/entityId, и старые, созданные до появления этих полей —
   * единообразно, одним и тем же ключом, а не двумя разными группами на одну заявку.
   * Список уже отсортирован новые-сверху (backend: ORDER BY createdAt DESC) — первое
   * попавшееся уведомление с данным ключом при обходе и есть самое свежее в группе,
   * порядок групп сохраняется.
   */
  private groupNotifications(items: NotificationDto[]): NotificationGroup[] {
    const groups: NotificationGroup[] = [];
    const byKey = new Map<string, NotificationGroup>();

    for (const item of items) {
      const key = item.url ? `${item.title}|${item.url}` : `single:${item.id}`;
      let group = byKey.get(key);
      if (!group) {
        group = { key, latest: item, items: [], unreadCount: 0 };
        byKey.set(key, group);
        groups.push(group);
      }
      group.items.push(item);
      if (!item.read) group.unreadCount += 1;
    }

    return groups;
  }

  /** Открыть группу уведомлений: перейти по url самого свежего и отметить прочитанной всю группу. */
  openNotification(group: NotificationGroup): void {
    this.groups = this.groups.filter(g => g.key !== group.key);
    this.notifications = this.notifications.filter(n => !group.items.some(gi => gi.id === n.id));
    this.unreadCount = Math.max(0, this.unreadCount - group.unreadCount);
    this.open = false;
    this.cdr.markForCheck();

    group.items.filter(n => !n.read).forEach(n => {
      this.notificationsService.markAsRead(n.id).subscribe();
    });
    const target = this.targetUrl(group.latest);
    if (target) {
      this.router.navigateByUrl(target);
    }
  }

  markAllAsRead(): void {
    if (this.notifications.length === 0 && this.unreadCount === 0) return;
    this.notificationsService.markAllAsRead().subscribe(() => {
      this.notifications = [];
      this.groups = [];
      this.unreadCount = 0;
      this.cdr.markForCheck();
    });
  }

  /** Номер запроса из уведомления: entityId или «#83» в заголовке/тексте (сервер пока пишет так). */
  private requestIdOf(n: NotificationDto): number | null {
    if (n.type === 'CHAT_REQUEST_MESSAGE' && n.entityId) return n.entityId;
    const m = (n.title + ' ' + (n.body || '')).match(/#(\d+)/);
    return m ? +m[1] : null;
  }

  /** Уведомление о ставке («Ваша ставка принята» и т.п.) — ведём на страницу запроса, где видна сама ставка. */
  private isBidNotice(n: NotificationDto): boolean {
    return /^Ваша ставка/i.test(n.title || '') || this.isLostNotice(n);
  }

  /** «Тендер не выигран» (так пишет сервер) — показываем мягче и понятнее. */
  private isLostNotice(n: NotificationDto): boolean {
    return /^Тендер не выигран/i.test(n.title || '');
  }

  /**
   * Заголовок для показа: «#83» → «R-26-83», а у уведомлений о ставке добавляем номер запроса —
   * «Ваша ставка принята по запросу R-26-83».
   */
  displayTitle(group: NotificationGroup): string {
    const n = group.latest;
    const id = this.requestIdOf(n);
    // «#38» → D-26-38 для сделок, R-26-38 для запросов
    const isDeal = n.type === 'CHAT_DEAL_MESSAGE' || /сделк/i.test(n.title || '');
    let title = this.isLostNotice(n)
      ? (this.languageService.translate('notifications.bidNotChosen') || 'Ваша ставка не выбрана')
      : (n.title || '').replace(/#(\d+)/g, (_, num) => formatDocNumber(isDeal ? 'D' : 'R', +num));
    if (id && this.isBidNotice(n) && !/#\d+/.test(n.title || '')) {
      title += ' ' + (this.languageService.translate('notifications.byRequest') || 'по запросу') + ' ' + formatRequestNumber(id);
    }
    return title;
  }

  /** Куда вести по клику: для ставок — на страницу запроса с этой ставкой, иначе — url с сервера. */
  private targetUrl(n: NotificationDto): string | null {
    const id = this.requestIdOf(n);
    if (id && this.isBidNotice(n)) {
      // Раздел кабинета берём из url сервера (/freight-forwarder/..., /customs-broker/...), по умолчанию — экспедитор
      const base = n.url?.match(/^\/[^/]+/)?.[0] || '/freight-forwarder';
      return `${base}/request/${id}`;
    }
    // Сообщение в чате — открываем страницу сразу с окном чата
    if (n.url && (n.type === 'CHAT_DEAL_MESSAGE' || n.type === 'CHAT_REQUEST_MESSAGE')) {
      return n.url + (n.url.includes('?') ? '&' : '?') + 'chat=1';
    }
    return n.url || null;
  }

  iconFor(group: NotificationGroup): 'chat' | 'generic' {
    const isChatType = (n: NotificationDto) => n.type === 'CHAT_REQUEST_MESSAGE' || n.type === 'CHAT_DEAL_MESSAGE';
    if (group.items.some(isChatType)) return 'chat';
    // Старые уведомления без type — определяем по тексту заголовка (задаётся бэкендом стабильно).
    return group.latest.title?.startsWith('Новое сообщение') ? 'chat' : 'generic';
  }

  timeAgo(iso: string): string {
    const date = new Date(iso);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return this.languageService.translate('notifications.justNow') || 'только что';
    const minutesAgoSuffix = this.languageService.translate('notifications.minutesAgoSuffix') || 'мин назад';
    if (diffMin < 60) return `${diffMin} ${minutesAgoSuffix}`;
    const diffHours = Math.floor(diffMin / 60);
    const hoursAgoSuffix = this.languageService.translate('notifications.hoursAgoSuffix') || 'ч назад';
    if (diffHours < 24) return `${diffHours} ${hoursAgoSuffix}`;
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', { day: 'numeric', month: 'short' });
  }
}
